/**
 * RsvpSection Component
 * Displays RSVP summary and attendee table for a session. Read-only.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Loader,
  Center,
  Table,
  Paper,
  ThemeIcon,
  Avatar,
} from '@mantine/core';
import { Mail } from 'react-feather';
import { sessionsApi } from '../../../../services/api/sessions';
import type { SessionRsvpDetails } from '../../../../types';
// MED-1044 — shared RSVP pill config (was duplicated inline here).
import { RSVP_STATUS_CONFIG } from '../../../../config/statusConfig';

function getInitials(name: string | undefined | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface RsvpSectionProps {
  cohortId: string;
  sessionId: string;
}

export function RsvpSection({ cohortId, sessionId }: RsvpSectionProps) {
  const [loading, setLoading] = useState(true);
  const [rsvpData, setRsvpData] = useState<SessionRsvpDetails | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await sessionsApi.getSessionRsvp(cohortId, sessionId);
      setRsvpData(data);
    } catch {
      // MED-1393: a failed read is not "invites not sent"; say it failed.
      setRsvpData(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [cohortId, sessionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (!rsvpData || rsvpData.attendees.length === 0) {
    return (
      <Paper withBorder p="lg" style={{ borderStyle: 'dashed' }}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={40} radius="xl" variant="light" color="gray">
            <Mail size={20} />
          </ThemeIcon>
          <Text size="sm" c="dimmed" ta="center">
            {loadFailed
              ? 'Could not load RSVPs.'
              : rsvpData?.is_finalized
                ? 'No RSVP responses yet.'
                : 'Session invites have not been sent yet.'}
          </Text>
        </Stack>
      </Paper>
    );
  }

  const { summary, attendees } = rsvpData;

  return (
    <Stack gap="md">
      {/* Summary badges */}
      <Group gap="md">
        <Badge color="green" variant="light">
          {summary.accepted} Accepted
        </Badge>
        <Badge color="red" variant="light">
          {summary.declined} Declined
        </Badge>
        <Badge color="yellow" variant="light">
          {summary.tentative} Tentative
        </Badge>
        <Badge color="gray" variant="light">
          {summary.pending} Awaiting
        </Badge>
      </Group>

      {/* Attendee table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Participant</Table.Th>
            <Table.Th>RSVP Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {attendees.map((a) => {
            const statusConfig = RSVP_STATUS_CONFIG[a.rsvp_status] || {
              label: a.rsvp_status,
              color: 'gray',
            };
            return (
              <Table.Tr key={a.user_id}>
                <Table.Td>
                  <Group gap="sm">
                    <Avatar size="sm" radius="xl" color="blue">
                      {getInitials(a.user_name)}
                    </Avatar>
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {a.user_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {a.user_email}
                      </Text>
                    </Stack>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusConfig.color} variant="light">
                    {statusConfig.label}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

export default RsvpSection;
